import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import SwiperCore, { Autoplay, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import Footer from '../../components/Footer';
import { Header } from '../../components/Header';
import ErrorBoundary from '../../utils/ErrorBoundaries';
import { Card } from './components/Card';
import { CommentsModal } from './components/CommentsModal';

import { apiNode, apiPHP } from '../../services/api';
import { swiperBreakpointsOptions } from '../../utils/swiperBreakpointsOptions';
import { NavigateToTournamentsPage } from '../../utils/NavigateToTournamentsPage';

import { buildHomeSections, buildTournamentGames } from './adapters';
import type { HomeGame, HomeResponse, HomeSection, TorneioListItem } from './types';

import background1 from '../../assets/img/banner1.jpg';
import background2 from '../../assets/img/banner2.jpg';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Banner, Container, GameList, GamesContainer, Row } from './styles';

export const Home: React.FC = () => {
  const navigate = useNavigate();
  SwiperCore.use([Autoplay]);

  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [gameId, setGameId] = useState(0);
  const [sections, setSections] = useState<HomeSection[]>([]);
  const [tournaments, setTournaments] = useState<HomeGame[]>([]);

  useEffect(() => {
    const getSections = async () => {
      try {
        const { data } = await apiNode.get<HomeResponse>('/home');
        setSections(buildHomeSections(data));
      } catch (err) {
        console.log(err);
      }
    };

    getSections();
  }, []);

  useEffect(() => {
    const getTournaments = async () => {
      try {
        const { data } = await apiPHP.get<TorneioListItem[]>('/jogo/torneios');
        setTournaments(buildTournamentGames(data));
      } catch (err) {
        console.log(err);
      }
    };

    getTournaments();
  }, []);

  const handleOpenCommentsModal = (id: number) => {
    setGameId(id);
    setIsCommentsModalOpen(true);
  };

  return (
    <Container>
      <Header />
      <Banner>
        <Swiper
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          loop={true}
          navigation={true}
          modules={[Pagination, Navigation]}
          className="mySwiper"
        >
          <SwiperSlide>
            <img src={background1} alt="Logo 1" />
            <span className="shadow" />
          </SwiperSlide>
          <SwiperSlide>
            <img src={background2} alt="Logo 2" />
            <span className="shadow" />
          </SwiperSlide>
        </Swiper>
      </Banner>

      <ErrorBoundary>
        <GamesContainer>
          <h1>Jogue Agora!</h1>
          <GameList>
            {sections.map((section) => (
              <a key={section.anchor} href={`#${section.anchor}`}>
                <span>{section.name}</span>
              </a>
            ))}
            {tournaments.length > 0 && (
              <a href="#torneios">
                <span>torneios</span>
              </a>
            )}
          </GameList>

          {sections.map((section) => (
            <Row id={section.anchor} key={section.id}>
              <h2>{section.name}</h2>
              <Swiper
                loop={true}
                loopFillGroupWithBlank={true}
                navigation={true}
                modules={[Pagination, Navigation]}
                breakpoints={swiperBreakpointsOptions}
                className="mySwiper"
              >
                {section.jogos.map((game) => (
                  <SwiperSlide key={`${section.id}-${game.id_jogo}`}>
                    <Card
                      game={game}
                      showcase={section.showcase}
                      openCommentsModal={handleOpenCommentsModal}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Row>
          ))}

          {tournaments.length > 0 && (
            <Row id="torneios">
              <h2>Torneios</h2>
              <Swiper
                loop={true}
                navigation={true}
                modules={[Pagination, Navigation]}
                className="mySwiper"
                breakpoints={swiperBreakpointsOptions}
              >
                {tournaments.map((tournament) => (
                  <SwiperSlide key={`torneios-${tournament.id_jogo}`}>
                    <Card
                      game={tournament}
                      navigateToTournaments={() =>
                        NavigateToTournamentsPage(navigate, tournament.id_jogo)
                      }
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Row>
          )}
        </GamesContainer>
      </ErrorBoundary>

      <Footer />

      <CommentsModal
        isOpen={isCommentsModalOpen}
        closeModal={() => setIsCommentsModalOpen(false)}
        gameId={gameId}
        updatePassatemposCommentsCount={() => setIsCommentsModalOpen(false)}
      />
    </Container>
  );
};
